import { Button, Group, Paper, Stack, Text, Title, Select } from "@mantine/core";
import { DatePicker, TimePicker } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { RenderJson } from "octalog-base";
import { useEffect, useRef } from "react";

import { Duration } from "../../../../../../common/calendar/Duration";
import { formatDate } from "../../../../../../common/UnitFormatter";
import { nameOf } from "../../../../../../common/Utils";
import type {
  OrderDto,
  OrderStatus,
  OrderDeliveryScheduleDto,
} from "../../../../../../generated/client/octalog-service";
import { OrderStatusBadge } from "../../../../components/OrderStatusBadge/OrderStatusBadge";
import { useScheduleDeliveryMutation } from "../../../../mutation/useScheduleDeliveryMutation";

import classes from "./OrderDetailPanelContent.module.css";

interface OrderDetailPanelContentProps {
  order: OrderDto;
}

export function OrderDetailPanelContent({ order }: OrderDetailPanelContentProps) {
  const deliveryScheduleMutation = useScheduleDeliveryMutation();

  const form = useForm<OrderDeliveryScheduleDto>({
    initialValues: {
      orderNumber: order.orderNumber,
      deliveryDate: order.deliverySchedule?.deliveryDate ?? "",
      deliveryTime: order.deliverySchedule?.deliveryTime ?? "",
      duration: order.deliverySchedule?.duration ?? "PT1H",
    },

    validate: {
      orderNumber: (value: string | undefined) => (value ? null : "Bitte wählen Sie eine Bestellung aus"),
      deliveryDate: (value: string | null) => (value ? null : "Bitte geben Sie ein Datum ein"),
      deliveryTime: (value: string | null) => (value ? null : "Bitte geben Sie eine Uhrzeit ein"),
      duration: (value: string | undefined) => (value ? null : "Bitte geben Sie eine Dauer ein"),
    },
  });

  const prevOrderRef = useRef({
    orderNumber: order.orderNumber,
    deliveryDate: order.deliverySchedule?.deliveryDate,
    deliveryTime: order.deliverySchedule?.deliveryTime,
    duration: order.deliverySchedule?.duration,
  });

  useEffect(() => {
    // Check if the order itself changed (not the form)
    const orderChanged =
      prevOrderRef.current.orderNumber !== order.orderNumber ||
      prevOrderRef.current.deliveryDate !== order.deliverySchedule?.deliveryDate ||
      prevOrderRef.current.deliveryTime !== order.deliverySchedule?.deliveryTime ||
      prevOrderRef.current.duration !== order.deliverySchedule?.duration;

    // Only update form when order changes, not when user edits form
    if (orderChanged) {
      form.setValues({
        orderNumber: order.orderNumber,
        deliveryDate: order.deliverySchedule?.deliveryDate ?? "",
        deliveryTime: order.deliverySchedule?.deliveryTime ?? "",
        duration: order.deliverySchedule?.duration ?? "PT1H",
      });

      // Update ref to current order values
      prevOrderRef.current = {
        orderNumber: order.orderNumber,
        deliveryDate: order.deliverySchedule?.deliveryDate,
        deliveryTime: order.deliverySchedule?.deliveryTime,
        duration: order.deliverySchedule?.duration,
      };
    }
  }, [
    order.orderNumber,
    order.deliverySchedule?.deliveryDate,
    order.deliverySchedule?.deliveryTime,
    order.deliverySchedule?.duration,
    form,
  ]);

  const durationData = [
    { value: "PT15M", label: "15 Minuten" },
    { value: "PT30M", label: "30 Minuten" },
    { value: "PT45M", label: "45 Minuten" },
    { value: "PT1H", label: "1 Stunde" },
    { value: "PT1H15M", label: "1 Stunde 15 Minuten" },
    { value: "PT1H30M", label: "1 Stunde 30 Minuten" },
    { value: "PT1H45M", label: "1 Stunde 45 Minuten" },
    { value: "PT2H", label: "2 Stunden" },
    { value: "PT2H15M", label: "2 Stunden 15 Minuten" },
    { value: "PT2H30M", label: "2 Stunden 30 Minuten" },
    { value: "PT2H45M", label: "2 Stunden 45 Minuten" },
    { value: "PT3H", label: "3 Stunden" },
    { value: "PT3H15M", label: "3 Stunden 15 Minuten" },
    { value: "PT3H30M", label: "3 Stunden 30 Minuten" },
    { value: "PT3H45M", label: "3 Stunden 45 Minuten" },
  ];

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        deliveryScheduleMutation.mutate({
          body: values,
        });
      })}
    >
      <Stack className={classes.container}>
        <Title order={4}>Bestellung #{order.orderNumber}</Title>

        <Paper className={classes.section} p="md" withBorder>
          <Stack gap="md">
            <div>
              <Text className={classes.label}>Kunde</Text>
              <Text className={classes.value} fw={500}>
                {order.invoiceAddress.split("\n")[0]}
              </Text>
              <Text className={classes.value} size="sm">
                {order.invoiceAddress.split("\n").slice(1).join(", ")}
              </Text>
            </div>
            <div>
              <Text className={classes.label}>Status</Text>
              <OrderStatusBadge size="lg" status={order.status as OrderStatus} />
            </div>
            <div>
              <Text className={classes.label}>Lieferadresse</Text>
              <Text className={classes.value}>{order.deliveryAddress || order.invoiceAddress}</Text>
            </div>
            <div>
              <Text className={classes.label}>Gewünschter Lieferzeitraum</Text>
              <Text className={classes.value} fw={500}>
                {formatDate(order.deliveryTargetDateStart)}
                {order.deliveryTargetDateEnd ? ` - ${formatDate(order.deliveryTargetDateEnd)}` : ""}
              </Text>
              <Text className={classes.value} size="sm">
                {order.deliveryTargetTimeStart ? ` ${order.deliveryTargetTimeStart}` : ""}
                {order.deliveryTargetTimeEnd ? ` - ${order.deliveryTargetTimeEnd}` : ""}
              </Text>
            </div>
            {order.deliverySchedule ? (
              <div>
                <Text className={classes.label}>Geplante Lieferung</Text>
                <Text className={classes.value} fw={500}>
                  {formatDate(order.deliverySchedule.deliveryDate)} {order.deliverySchedule.deliveryTime}
                </Text>
                <Text className={classes.value} size="sm">
                  Dauer: {Duration.parse(order.deliverySchedule.duration).humanize()}
                </Text>
              </div>
            ) : null}
          </Stack>
        </Paper>

        <Paper className={classes.section} p="md" withBorder>
          <Text className={classes.label}>Geplante Lieferung</Text>
          <Stack>
            <DatePicker {...form.getInputProps(`${nameOf<OrderDeliveryScheduleDto>().deliveryDate}`)} />
            <Text size="sm">Start</Text>
            <TimePicker {...form.getInputProps(`${nameOf<OrderDeliveryScheduleDto>().deliveryTime}`)} />
            <Text>Dauer</Text>
            <Select data={durationData} {...form.getInputProps(`${nameOf<OrderDeliveryScheduleDto>().duration}`)} />
          </Stack>
        </Paper>

        <Group className={classes.buttonGroup} justify="center">
          <Button
            color="gray"
            onClick={() => {
              form.reset();
            }}
            variant="light"
          >
            Zurücksetzen
          </Button>
          <Button type="submit">Speichern</Button>
        </Group>

        <RenderJson data={order} h={700} w={800} />
      </Stack>
    </form>
  );
}
